import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  AtSign,
  ChevronRight,
  Clock,
  ExternalLink,
  Mail,
  MessageCircle,
} from "lucide-react";
import {
  contactChannelsWithValue,
  type ContactChannel,
} from "../components/PublicChrome/publicSitePolicy";
import "./Contact.view.css";

const CONTACT_HOURS =
  "Segunda a sexta, das 8h às 18h (horário de Brasília)";

function channelBody(channel: ContactChannel): string {
  if (channel.id === "whatsapp") {
    return "Para dúvidas sobre planos, pagamento e acesso.";
  }
  if (channel.id === "instagram") {
    return "Novidades e conteúdo de estudo.";
  }
  return "Para assuntos que precisam de mais detalhes.";
}

function channelAction(channel: ContactChannel): string {
  if (channel.id === "whatsapp") {
    return "Abrir conversa no WhatsApp";
  }
  if (channel.id === "instagram") {
    return "Abrir o Instagram";
  }
  return "Escrever um e-mail";
}

function opensInNewTab(href: string): boolean {
  return href.startsWith("https://") || href.startsWith("http://");
}

function ChannelIcon({ channel }: { channel: ContactChannel }) {
  if (channel.id === "whatsapp") {
    return <MessageCircle size={26} />;
  }
  if (channel.id === "instagram") {
    return <AtSign size={26} />;
  }
  return <Mail size={26} />;
}

function ContactChannelCard({ channel }: { channel: ContactChannel }) {
  const external = opensInNewTab(channel.href);
  const showValue = channel.id === "instagram" || channel.id === "email";

  return (
    <article className="contato-card">
      <span
        className={`contato-card__icon contato-card__icon--${channel.id}`}
        aria-hidden="true"
      >
        <ChannelIcon channel={channel} />
      </span>
      <h2 className="contato-card__title">{channel.label}</h2>
      <p className="contato-card__body">{channelBody(channel)}</p>
      {showValue ? (
        <p className="contato-card__value">{channel.value}</p>
      ) : null}
      <a
        className="contato-action"
        href={channel.href}
        {...(external
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
      >
        {channelAction(channel)}
        {external ? (
          <>
            <span className="sr-only"> (abre em nova aba)</span>
            <ExternalLink aria-hidden="true" size={18} />
          </>
        ) : (
          <ChevronRight aria-hidden="true" size={18} />
        )}
      </a>
    </article>
  );
}

export default function Contact() {
  useEffect(() => {
    document.title = "Contato | Acertei Questões";
  }, []);

  const channels = contactChannelsWithValue();

  return (
    <article className="contato">
      <section className="contato-hero" aria-labelledby="contato-title">
        <div className="contato-hero__deco" aria-hidden="true">
          <span className="contato-hero__shape contato-hero__shape--teal" />
          <span className="contato-hero__shape contato-hero__shape--emerald" />
          <span className="contato-hero__shape contato-hero__shape--amber" />
          <span className="contato-hero__shape contato-hero__shape--sky" />
        </div>
        <div className="contato-hero__copy">
          <p className="contato-hero__eyebrow">CONTATO</p>
          <h1 id="contato-title" className="contato-hero__title">
            Fale com a gente
          </h1>
          <p className="contato-hero__lead">
            Escolha o canal que for melhor para você.
          </p>
          <div className="contato-hero__hours">
            <p className="contato-hero__hours-label">
              <Clock aria-hidden="true" size={18} />
              Horário de atendimento:
            </p>
            <p className="contato-hero__hours-value">{CONTACT_HOURS}</p>
          </div>
        </div>
      </section>

      {channels.length > 0 ? (
        <section className="contato-channels" aria-label="Canais de contato">
          <div className="contato-channels__row">
            {channels.map((channel) => (
              <ContactChannelCard key={channel.id} channel={channel} />
            ))}
          </div>
        </section>
      ) : null}

      <section className="contato-next" aria-labelledby="contato-next-title">
        <h2 id="contato-next-title" className="contato-next__title">
          Antes de escrever
        </h2>
        <p className="contato-next__lead">Talvez sua dúvida já tenha resposta:</p>
        <nav className="contato-next__links" aria-label="Antes de escrever">
          <Link className="contato-next__link" to="/#duvidas">
            Dúvidas frequentes
            <ChevronRight aria-hidden="true" size={18} />
          </Link>
          <Link className="contato-next__link" to="/reembolso">
            Reembolso e cancelamento
            <ChevronRight aria-hidden="true" size={18} />
          </Link>
          <Link className="contato-next__link" to="/planos">
            Ver planos e preços
            <ChevronRight aria-hidden="true" size={18} />
          </Link>
        </nav>
      </section>
    </article>
  );
}
